import {
  businessAuditLogTableColumns,
  CustomerTableColumns,
  errorLogTableColumns,
  InCareNetTableColumns,
  InPaceTableColumns,
  NetECGTableColumns,
  salesPersonTableColumns
} from "../constants";
import Column from "../models/Column";
import '../App.css'
import {Box} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import CircleIcon from "@mui/icons-material/Circle";
import React from "react";
import LMTable, {TableOptions} from "./Tables/LMTable";
import TooltipWarning from "./subcomponents/TooltipIcons/TooltipWarning";
import {CellParams, LMColumn, LMTableApi, PaginationModel, SortModel} from "./Tables/types";

export default class TableFactory {

  /**
   * createColumns is pure over (subject, columnFields) and createTable is called
   * straight from its callers' JSX, so building the array inline handed LMTable a new
   * `columns` identity on every render — defeating every memo inside it (re-deriving
   * the react-table column defs, re-sorting, and re-running the apiRef effect, which
   * nulls then re-assigns apiRef.current each time). Cache by the arguments instead,
   * so all nine call sites get a stable array without having to memoize themselves.
   */
  private static columnCache = new Map<string, LMColumn[]>()

  private static createColumnsCached(subject: string, columnFields: string[]): LMColumn[] {
    const key = `${subject}|${columnFields.join(',')}`
    let columns = this.columnCache.get(key)
    if (!columns) {
      columns = this.createColumns(subject, columnFields)
      this.columnCache.set(key, columns)
    }
    return columns
  }

  private static createColumns(subject: string, columnFields: string[]): LMColumn[] {
    switch (subject) {
      case "inCareNetHF":
        return this.filterColumnsAndApplyStyles(InCareNetTableColumns, columnFields)
      case "inPace":
        return this.filterColumnsAndApplyStyles(InPaceTableColumns, columnFields)
      case "customers":
        return this.filterColumnsAndApplyStyles(CustomerTableColumns, columnFields)
      case "salesperson":
        return this.filterColumnsAndApplyStyles(salesPersonTableColumns, columnFields)
      case "businessAuditLog":
        return this.filterColumnsAndApplyStyles(businessAuditLogTableColumns, columnFields)
      case "errorLog":
        return this.filterColumnsAndApplyStyles(errorLogTableColumns, columnFields)
      case "netECG":
        return this.filterColumnsAndApplyStyles(NetECGTableColumns, columnFields)
      default: //TODO add other subjects
        return []
    }
  }

  private static filterColumnsAndApplyStyles(columns: Column[], columnFields: string[]): LMColumn[] {
    return this.applyStyles(this.filterColumns(columns, columnFields))
  }

  private static filterColumns(columns: Column[], columnFields: string[]) {
    return columns.filter((column) => columnFields.includes(column.field))
  }

  private static applyStyles(columns: Column[]): LMColumn[] {
    return columns.map((column): LMColumn => {
      // This fragment sets the width of the column. "flex" is prioritized before "width"
      let columnWidth = 100 // Default static column width
      let staticWidth = true
      if (column.flex) {
        columnWidth = column.flex
        staticWidth = false
      } else if (column.width) {
        columnWidth = column.width
      }

      return {
        field: column.field,
        headerName: column.headerName,
        ...(column.renderCell ? {renderCell: column.renderCell} : {}),
        ...(column.sortComparator ? {sortComparator: column.sortComparator} : {}),
        ...(column.iconFromBool ? {
          renderCell: (params: CellParams) => {
            return (<Box sx={{marginTop: "7px"}}>{params.value ? <DoneIcon/> : <CloseIcon/>}</Box>)
          }
        } : {}),
        ...(column.statusIcon ? {
          renderCell: (params: CellParams) => {
            const colorMap: Record<string, string> = {
              success: "success.main",
              partial: "warning.main",
              failed: "error.main",
            }
            const labelMap: Record<string, string> = {
              success: "Refresh successful",
              partial: "Refresh partially failed",
              failed: "Refresh failed",
            }
            const color = colorMap[params.value] ?? "grey.400"
            const label = labelMap[params.value] ?? "Refresh status unknown"
            return (
              <Box sx={{marginTop: "7px"}} role="img" aria-label={label} title={label}>
                <CircleIcon sx={{color, fontSize: 16}}/>
              </Box>
            )
          }
        } : {}),
        ...(column.formatTimestamp ? {
          renderCell: (params: CellParams) => {
            if (params.value == null) return <div>–</div>
            return <div>{new Date(params.value).toLocaleString(undefined, {timeZone: 'Europe/Berlin'})}</div>
          }
        } : {}),
        ...(column.warnIconIfError ? {
          renderCell: (params: CellParams) => {
            if (column.warnIconIfError === undefined) { // This check is only for the IDE which needs a check if undefined for warnIconIfError
              return params.value
            }
            const check = column.warnIconIfError(params.value)
            return (
              check.error ?
                <Box sx={{marginTop: "7px"}}>
                  <TooltipWarning tooltipMessage={check.message}/>
                </Box> :
                <div>{params.value}</div>
            )
          }
        } : {}),
        ...(staticWidth ? {width: columnWidth} : {flex: columnWidth}),
      }
    })
  }

  static createTable(
    subject: string,
    initialColumnFields: string[],
    rows: any[],
    selectionModel: string[],
    selectionHandler: (ids: string[]) => void,
    onSortModelChange: (model: SortModel) => void,
    options: TableOptions = {},
    apiRef: React.MutableRefObject<LMTableApi | null>,
    paginationModel?: PaginationModel,
    onPaginationModelChange?: (model: PaginationModel) => void,
    rowCount?: number,
    loading?: boolean,
    showFullContent: boolean = false
  ) {

    return (
      <LMTable
        onSortModelChange={onSortModelChange}
        rows={rows}
        columns={this.createColumnsCached(subject, initialColumnFields)}
        options={options}
        selectionModel={selectionModel}
        selectionHandler={selectionHandler}
        apiRef={apiRef}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={rowCount}
        loading={loading}
        showFullContent={showFullContent}
      />
    )
  }
}
